<template>
  <div class="date-range-picker">
    <!-- Date Input Display -->
    <div class="date-input" @click="togglePicker">
      <input
        type="text"
        :value="displayValue"
        placeholder="Select date range"
        readonly
        class="date-input-field"
      />
      <svg
        class="calendar-icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
          stroke="#6C737F"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M16 2V6"
          stroke="#6C737F"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M8 2V6"
          stroke="#6C737F"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M3 10H21"
          stroke="#6C737F"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <!-- Calendar Popup -->
    <Transition name="calendar-fade">
      <div v-if="isOpen" class="calendar-container" ref="calendarRef">
        <div class="calendar-header">
          <button @click="previousMonth" class="nav-button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="#6C737F"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div class="month-year">
            {{ currentMonthYear }}
          </div>
          <button @click="nextMonth" class="nav-button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18L15 12L9 6"
                stroke="#6C737F"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <!-- Weekdays -->
        <div class="weekdays">
          <div v-for="day in weekdays" :key="day" class="weekday">
            {{ day }}
          </div>
        </div>

        <!-- Calendar Days -->
        <div class="calendar-days">
          <div
            v-for="(day, index) in calendarDays"
            :key="index"
            class="day-cell"
            :class="{
              'other-month': !day.isCurrentMonth,
              'selected-start': isStartDate(day),
              'selected-end': isEndDate(day),
              'in-range': isInRange(day),
              today: isToday(day),
              'has-event': day.hasEvent,
            }"
            @click="selectDate(day)"
          >
            <span class="day-number">{{ day.day }}</span>
            <span v-if="day.hasEvent" class="event-dot"></span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="calendar-actions">
          <button @click="resetDate" class="btn-reset">Reset Date</button>
          <div class="calendar-actions-right">
            <button @click="cancelSelection" class="btn-cancel">Cancel</button>
            <button @click="applySelection" class="btn-apply">Apply</button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  defineProps,
  defineEmits,
} from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ from: null, to: null }),
  },
});

const emit = defineEmits(["update:modelValue", "apply"]);

const isOpen = ref(false);
const calendarRef = ref(null);
const currentDate = ref(new Date());
const selectedStart = ref(null);
const selectedEnd = ref(null);
const tempStart = ref(null);
const tempEnd = ref(null);

const weekdays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const currentMonthYear = computed(() => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${months[currentDate.value.getMonth()]} ${currentDate.value.getFullYear()}`;
});

const displayValue = computed(() => {
  if (selectedStart.value && selectedEnd.value) {
    return `${formatDate(selectedStart.value)} - ${formatDate(selectedEnd.value)}`;
  } else if (selectedStart.value) {
    return formatDate(selectedStart.value);
  }
  return "";
});

const calendarDays = computed(() => {
  const days = [];
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();

  // Get first day of month
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Get starting point (Sunday before first day)
  const startingDayOfWeek = firstDay.getDay();

  // Add previous month's days
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    days.push({
      day: prevMonthLastDay - i,
      date: new Date(year, month - 1, prevMonthLastDay - i),
      isCurrentMonth: false,
      hasEvent: false,
    });
  }

  // Add current month's days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const date = new Date(year, month, i);
    days.push({
      day: i,
      date: date,
      isCurrentMonth: true,
      hasEvent: i === 4, // Example: Add event marker on 4th
    });
  }

  // Add next month's days to fill the grid
  const remainingDays = 42 - days.length; // 6 rows * 7 days
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      day: i,
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
      hasEvent: false,
    });
  }

  return days;
});

const formatDate = (date) => {
  if (!date) return "";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[date.getMonth()]} ${String(date.getDate()).padStart(2, "0")}, ${date.getFullYear()}`;
};

const isStartDate = (day) => {
  if (!tempStart.value) return false;
  return isSameDay(day.date, tempStart.value);
};

const isEndDate = (day) => {
  if (!tempEnd.value) return false;
  return isSameDay(day.date, tempEnd.value);
};

const isInRange = (day) => {
  if (!tempStart.value || !tempEnd.value) return false;
  return day.date > tempStart.value && day.date < tempEnd.value;
};

const isToday = (day) => {
  const today = new Date();
  return isSameDay(day.date, today);
};

const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

const selectDate = (day) => {
  if (!day.isCurrentMonth) return;

  if (!tempStart.value || (tempStart.value && tempEnd.value)) {
    // Start new selection
    tempStart.value = day.date;
    tempEnd.value = null;
  } else {
    // Complete the range
    if (day.date < tempStart.value) {
      tempEnd.value = tempStart.value;
      tempStart.value = day.date;
    } else {
      tempEnd.value = day.date;
    }
  }
};

const togglePicker = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    tempStart.value = selectedStart.value;
    tempEnd.value = selectedEnd.value;
  }
};

const previousMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() - 1,
    1
  );
};

const nextMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1,
    1
  );
};

const cancelSelection = () => {
  tempStart.value = selectedStart.value;
  tempEnd.value = selectedEnd.value;
  isOpen.value = false;
};

const resetDate = () => {
  tempStart.value = null;
  tempEnd.value = null;
  selectedStart.value = null;
  selectedEnd.value = null;

  emit("update:modelValue", { from: null, to: null });
  emit("apply", { from: null, to: null });
  isOpen.value = false;
};

const applySelection = () => {
  selectedStart.value = tempStart.value;
  selectedEnd.value = tempEnd.value;

  const from = selectedStart.value
    ? selectedStart.value.toISOString().split("T")[0]
    : null;
  const to = selectedEnd.value
    ? selectedEnd.value.toISOString().split("T")[0]
    : null;

  emit("update:modelValue", { from, to });
  emit("apply", { from, to });
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  const pickerElement = event.target.closest(".date-range-picker");
  if (!pickerElement && isOpen.value) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  // Initialize from modelValue if provided
  if (props.modelValue?.from) {
    selectedStart.value = new Date(props.modelValue.from);
    tempStart.value = new Date(props.modelValue.from);
  }
  if (props.modelValue?.to) {
    selectedEnd.value = new Date(props.modelValue.to);
    tempEnd.value = new Date(props.modelValue.to);
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue?.from) {
      selectedStart.value = new Date(newValue.from);
      tempStart.value = new Date(newValue.from);
    } else {
      selectedStart.value = null;
      tempStart.value = null;
    }

    if (newValue?.to) {
      selectedEnd.value = new Date(newValue.to);
      tempEnd.value = new Date(newValue.to);
    } else {
      selectedEnd.value = null;
      tempEnd.value = null;
    }
  }
);
</script>

<style scoped>
.date-range-picker {
  position: relative;
  width: 100%;
}

.date-input {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #6c737f;
  border-radius: 8px;
  padding: 0 16px;
  height: 40px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.date-input:hover {
  border-color: var(--primary-blue);
}

.date-input-field {
  flex: 1;
  border: none;
  outline: none;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  color: #3b4648;
  background: transparent;
  cursor: pointer;
  letter-spacing: 0.024px;
  line-height: 24px;
}

.date-input-field::placeholder {
  color: #98a2b3;
}

.calendar-icon {
  flex-shrink: 0;
  margin-left: 16px;
}

.calendar-container {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border: 1px solid #d8dae5;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 428px;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  margin-bottom: 8px;
}

.nav-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.nav-button:hover svg path {
  stroke: var(--primary-blue);
}

.month-year {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  color: #3b4648;
  text-align: center;
  letter-spacing: 0.024px;
  line-height: 24px;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  background: #eaecff;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.weekday {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 500;
  color: #40444b;
  text-align: center;
  letter-spacing: 0.014px;
  line-height: 20px;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  margin-bottom: 8px;
}

.day-cell {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
}

.day-cell.other-month .day-number {
  color: #bac1cc;
}

.day-cell.selected-start,
.day-cell.selected-end {
  background: var(--primary-blue);
}

.day-cell.selected-start {
  border-radius: 8px 0 0 8px;
}

.day-cell.selected-end {
  border-radius: 0 8px 8px 0;
}

.day-cell.selected-start.selected-end {
  border-radius: 8px;
}

.day-cell.in-range {
  background: #f2f3fb;
  border-radius: 0;
}

.day-cell.selected-start .day-number,
.day-cell.selected-end .day-number {
  color: white;
}

.day-cell.in-range .day-number {
  color: #3b4648;
}

.day-cell:not(.other-month):hover:not(.selected-start):not(.selected-end) {
  background: #f9fafb;
}

.day-number {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  color: #3b4648;
  text-align: center;
  letter-spacing: 0.035px;
  line-height: 20px;
  z-index: 1;
}

.day-cell.has-event .event-dot {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background: var(--primary-blue);
  border-radius: 50%;
}

.day-cell.selected-start .event-dot,
.day-cell.selected-end .event-dot {
  background: white;
}

.calendar-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 8px;
  width: 100%;
}

.calendar-actions-right {
  display: flex;
  gap: 8px;
}

.btn-reset {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 700;
  padding: 10px 16px;
  border: none;
  background: none;
  color: var(--primary-blue);
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.014px;
  line-height: 20px;
  min-width: 106px;
  border-radius: 8px;
}

.btn-reset:hover {
  background: #f9fafb;
}

.btn-cancel,
.btn-apply {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 700;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.014px;
  line-height: 20px;
  min-width: 106px;
}

.btn-cancel {
  background: white;
  border: 1px solid var(--primary-blue);
  color: var(--primary-blue);
}

.btn-cancel:hover {
  background: #f9fafb;
}

.btn-apply {
  background: var(--primary-blue);
  border: 1px solid var(--primary-blue);
  color: white;
}

.btn-apply:hover {
  background: #00304f;
}

/* Animations */
.calendar-fade-enter-active,
.calendar-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.calendar-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.calendar-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Responsive */
@media (max-width: 768px) {
  .calendar-container {
    min-width: 320px;
  }
}
</style>
